import { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Plug, Truck, Percent, TableProperties, Boxes, Warehouse, Wallet, Receipt, ScrollText, Settings, LogOut, Store, Menu, X, LifeBuoy, Sparkles, BarChart3, Gauge, Workflow, AlertTriangle, Bot, ShieldAlert, Undo2, Scale, TrendingUp, Zap, ShieldCheck, Network, MapPin, FileCheck, Globe2, Code2, FileStack, IndianRupee } from "lucide-react";
import Logo from "./Logo";
import SessionBanner from "./SessionBanner";
import { SidebarNav } from "./DashboardLayout";
import { useAuth } from "../context/AuthContext";

const SECTIONS = [
  {
    title: "TOWER", items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/admin/insights", label: "Insights", icon: MapPin },
      { to: "/admin/ai", label: "Assistant", icon: Sparkles },
      { to: "/admin/profit", label: "Profit Insights", icon: TrendingUp },
    ],
  },
  {
    title: "NETWORK", items: [
      { to: "/admin/providers", label: "Providers", icon: Plug },
      { to: "/admin/couriers", label: "Couriers", icon: Truck },
      { to: "/admin/direct-couriers", label: "Direct Couriers", icon: Network },
      { to: "/admin/performance", label: "Performance", icon: Gauge },
      { to: "/admin/rules", label: "Rules Engine", icon: Workflow },
      { to: "/admin/automation", label: "Automation", icon: Zap },
    ],
  },
  {
    title: "EXCEPTIONS", items: [
      { to: "/admin/ndr", label: "NDR Center", icon: AlertTriangle },
      { to: "/admin/ndr-rules", label: "NDR Automation", icon: Bot },
      { to: "/admin/rto-guard", label: "RTO Guard", icon: ShieldAlert },
      { to: "/admin/returns", label: "Returns", icon: Undo2 },
      { to: "/admin/disputes", label: "Disputes", icon: Scale },
    ],
  },
  {
    title: "PEOPLE", items: [
      { to: "/admin/customers", label: "Customers", icon: Users },
      { to: "/admin/api-users", label: "API Users", icon: Code2 },
      { to: "/admin/kyc", label: "KYC Review", icon: FileCheck },
      { to: "/admin/team", label: "Team & Roles", icon: ShieldCheck },
      { to: "/admin/wallets", label: "Wallets", icon: Wallet },
      { to: "/admin/cod", label: "COD Settlements", icon: IndianRupee },
      { to: "/admin/support", label: "Support", icon: LifeBuoy },
    ],
  },
  {
    title: "FRANCHISE", items: [
      { to: "/admin/franchises", label: "Franchises", icon: Store },
      { to: "/admin/franchise-withdrawals", label: "Franchise Payouts", icon: IndianRupee },
    ],
  },
  {
    title: "PRICING", items: [
      { to: "/admin/pricing", label: "Pricing Engine", icon: Percent },
      { to: "/admin/rate-cards", label: "Rate Cards", icon: TableProperties },
    ],
  },
  {
    title: "OPERATIONS", items: [
      { to: "/admin/shipments", label: "Shipments", icon: Boxes },
      { to: "/admin/international", label: "International", icon: Globe2 },
      { to: "/admin/warehouses", label: "Warehouses", icon: Warehouse },
      { to: "/admin/manifests", label: "Manifests", icon: FileStack },
      { to: "/admin/billing", label: "Billing", icon: Receipt },
    ],
  },
  { title: "SYSTEM", items: [{ to: "/admin/logs", label: "Logs & Events", icon: ScrollText }, { to: "/admin/settings", label: "Settings", icon: Settings }] },
];

const ROLE_PERMS = {
  super_admin: ["*"], admin: ["*"],
  operations: ["shipments.view", "shipments.manage", "ndr.manage", "warehouse.manage", "providers.view", "rules.manage", "franchise.view"],
  support: ["support.manage", "ndr.manage", "shipments.view"],
  finance: ["billing.view", "wallets.manage", "cod.manage", "disputes.manage", "analytics.view", "franchise.view", "franchise.finance"],
  warehouse_manager: ["warehouse.manage", "inventory.manage", "shipments.view"],
};

const ITEM_PERM = {
  "/admin/ndr": "ndr.manage", "/admin/ndr-rules": "ndr.manage", "/admin/rto-guard": "ndr.manage", "/admin/returns": "ndr.manage",
  "/admin/support": "support.manage", "/admin/shipments": "shipments.view", "/admin/warehouses": "warehouse.manage",
  "/admin/wallets": "wallets.manage", "/admin/billing": "billing.view", "/admin/disputes": "disputes.manage",
  "/admin/analytics": "analytics.view", "/admin/providers": "providers.view", "/admin/performance": "providers.view", "/admin/rules": "rules.manage",
  "/admin/franchises": "franchise.view", "/admin/franchise-withdrawals": "franchise.finance",
};

export function staffSections(role) {
  const perms = ROLE_PERMS[role] || [];
  if (!perms.length) return [];
  return SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((it) => perms.includes("*") || (ITEM_PERM[it.to] && perms.includes(ITEM_PERM[it.to]))) }))
    .filter((s) => s.items.length);
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const sections = staffSections(user?.role);
  const roleLabel = (user?.role || "admin").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const doLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-shipany-navy lg:flex">
        <div className="px-5 py-5">
          <Logo dark />
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-shipany-cyan/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-shipany-cyan">
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-shipany-cyan" /> Control Tower
          </span>
        </div>
        <SidebarNav sections={sections} />
        <div className="border-t border-white/10 p-4">
          <Link to="/app" data-testid="admin-customer-view-link" className="mb-2 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white">
            <Store className="h-4 w-4" /> Customer View
          </Link>
          <button onClick={doLogout} data-testid="admin-logout-btn" className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-shipany-navy">
            <div className="flex items-center justify-between px-5 py-5">
              <Logo dark />
              <button onClick={() => setMobileOpen(false)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <SidebarNav sections={sections} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <SessionBanner />
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 glass-navy px-5 py-3.5">
          <button className="lg:hidden text-white" onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <p className="hidden text-xs font-semibold uppercase tracking-widest text-slate-400 lg:block">SHIPANY Control Tower</p>
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-shipany-cyan text-xs font-bold text-shipany-navy">{(user?.name || "A")[0].toUpperCase()}</span>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-white" data-testid="admin-user-name">{user?.name}</p>
              <p className="text-xs text-slate-400">{roleLabel}</p>
            </div>
          </div>
        </header>
        <main className="flex-1 p-5 sm:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
